import React, { useEffect, useRef, useState } from 'react';
import { Bell, ChevronLeft, ChevronRight, QrCode, Search, X } from 'lucide-react';

import { Colors, tone } from './theme';

/**
 * The app's own components, rebuilt for the browser.
 *
 * One file, because every one of them is a dozen lines and they are only ever
 * used together — the alternative is eight files that each import the same
 * three tokens. Each mirrors its counterpart in `mobile-frontend/src/components`
 * name for name, so a change made on the phone has one obvious place to land.
 */

/** The one badge. Same five tones as the app. */
export function StatusPill({ label, tone: name = 'neutral', dot, testID }) {
  const t = tone(name);
  return (
    <span
      data-testid={testID}
      className="inline-flex shrink-0 items-center gap-[5px] rounded-md border px-2 py-[3px] text-[11px] font-semibold tracking-[0.2px]"
      style={{ backgroundColor: t.bg, borderColor: t.border, color: t.fg }}
    >
      {dot && <span className="h-[5px] w-[5px] rounded-full" style={{ backgroundColor: t.fg }} />}
      <span className="truncate">{label}</span>
    </span>
  );
}

export function EmptyState({ icon: Icon, message, hint }) {
  return (
    <div className="flex flex-col items-center py-16 text-center">
      <Icon className="h-12 w-12" style={{ color: Colors.text.disabled }} strokeWidth={1.5} />
      <p className="mt-5 text-[15px] font-medium" style={{ color: Colors.text.secondary }}>{message}</p>
      {!!hint && (
        <p className="mt-2 max-w-[18rem] text-[13px]" style={{ color: Colors.text.tertiary }}>{hint}</p>
      )}
    </div>
  );
}

/**
 * One search box, for every list that has one. It holds its own text and hands
 * the query over once typing settles, so a six-letter customer name is one
 * request rather than six.
 */
export function SearchBar({ placeholder, onChange, delay = 350, testID, initialValue = '' }) {
  const [text, setText] = useState(initialValue);
  const settle = useRef(onChange);
  settle.current = onChange;

  useEffect(() => {
    const timer = setTimeout(() => settle.current(text.trim()), delay);
    return () => clearTimeout(timer);
  }, [text, delay]);

  return (
    <div
      className="flex h-[42px] items-center gap-2 rounded-[10px] border px-3"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <Search className="h-4 w-4 shrink-0" style={{ color: Colors.text.tertiary }} />
      <input
        data-testid={testID || 'search-input'}
        className="min-w-0 flex-1 bg-transparent text-[13px] font-medium outline-none"
        style={{ color: Colors.text.primary }}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        autoCapitalize="none"
        autoCorrect="off"
        type="search"
      />
      {!!text && (
        <button type="button" data-testid="search-clear" onClick={() => setText('')} aria-label="Clear search">
          <X className="h-4 w-4" style={{ color: Colors.text.tertiary }} />
        </button>
      )}
    </div>
  );
}

/**
 * The greeting, the name, and the two controls a screen reaches for from
 * anywhere — the scanner and the bell. The bell takes a callback rather than
 * navigating itself, so this file has no router import: half the app's tests
 * stub the router, and a kit that needed it would drag that stub into every
 * page that borrows a pill.
 */
export function AppHeader({ greeting, subtitle, unreadCount = 0, onScan, onNotifications, testID }) {
  return (
    <div className="flex items-center justify-between gap-3 pb-6 pt-6">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] font-semibold uppercase tracking-[1px]" style={{ color: Colors.text.secondary }}>
          {greeting}
        </p>
        <p className="mt-1 truncate text-[22px] font-bold -tracking-[0.5px]" style={{ color: Colors.text.primary }}>
          {subtitle}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {!!onScan && (
          <button
            type="button"
            data-testid="scan-qr-btn"
            onClick={onScan}
            aria-label="Scan a QR code"
            className="flex h-[52px] w-[52px] items-center justify-center rounded-full border shadow-sm"
            style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
          >
            <QrCode className="h-6 w-6" style={{ color: Colors.text.primary }} />
          </button>
        )}
        <button
          type="button"
          data-testid={testID || 'notifications-btn'}
          onClick={onNotifications}
          aria-label="Notifications"
          className="relative flex h-[52px] w-[52px] items-center justify-center rounded-full border shadow-sm"
          style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
        >
          <Bell className="h-6 w-6" style={{ color: Colors.text.primary }} />
          {unreadCount > 0 && (
            <span
              className="absolute right-1 top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full border-2 px-1 text-[10px] font-bold"
              style={{
                backgroundColor: Colors.status.error,
                borderColor: Colors.background.primary,
                color: Colors.text.inverse,
              }}
            >
              {unreadCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

/** The bar a stack screen wears instead of the greeting. */
export function TopBar({ eyebrow, title, subtitle, onBack, actions }) {
  return (
    <div
      className="flex items-center gap-3 border-b px-5 py-3"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <button
        type="button"
        data-testid="back-btn"
        onClick={onBack}
        aria-label="Back"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: Colors.background.secondary }}
      >
        <ChevronLeft className="h-[22px] w-[22px]" style={{ color: Colors.text.primary }} />
      </button>
      <div className="min-w-0 flex-1 text-center">
        {!!eyebrow && (
          <p className="truncate text-[10px] font-medium tracking-[1.5px]" style={{ color: Colors.text.secondary }}>
            {eyebrow}
          </p>
        )}
        <p className="truncate text-[16px] font-bold" style={{ color: Colors.text.primary }}>{title}</p>
        {!!subtitle && (
          <p className="truncate text-[11px] font-medium" style={{ color: Colors.text.tertiary }}>{subtitle}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">{actions}</div>
    </div>
  );
}

/** A round icon button, the size a thumb needs. */
export function IconButton({ icon: Icon, onClick, label, disabled, testID }) {
  return (
    <button
      type="button"
      data-testid={testID}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="flex h-9 w-9 items-center justify-center rounded-full border disabled:opacity-40"
      style={{ backgroundColor: Colors.background.secondary, borderColor: Colors.border.default }}
    >
      <Icon className="h-[18px] w-[18px]" style={{ color: Colors.accent.DEFAULT }} />
    </button>
  );
}

/** The app's spinner, at the size the screen asks for. */
export function Spinner({ size = 'lg', className = '' }) {
  const px = size === 'sm' ? 'h-4 w-4 border-2' : 'h-9 w-9 border-[3px]';
  return (
    <span
      className={`inline-block animate-spin rounded-full border-b-transparent ${px} ${className}`}
      style={{ borderColor: Colors.accent.DEFAULT, borderBottomColor: 'transparent' }}
      role="status"
      aria-label="Loading"
    />
  );
}

export function ScreenLoader() {
  return (
    <div className="flex flex-1 items-center justify-center py-24"><Spinner /></div>
  );
}

/**
 * A sheet at the bottom, where a thumb is — the app's own modal shape rather
 * than a dialog dropped in the middle of the screen.
 */
export function BottomSheet({ open, onClose, title, subtitle, children, testID }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid={testID}>
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/45"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full max-w-[26rem] rounded-t-[18px] px-5 pb-8 pt-3 shadow-2xl"
        style={{ backgroundColor: Colors.background.primary }}
      >
        <div className="mx-auto mb-4 h-1 w-10 rounded-full" style={{ backgroundColor: Colors.border.default }} />
        <p className="text-[17px] font-bold -tracking-[0.3px]" style={{ color: Colors.text.primary }}>{title}</p>
        {!!subtitle && (
          <p className="mb-4 mt-0.5 text-[13px] font-medium" style={{ color: Colors.text.secondary }}>{subtitle}</p>
        )}
        {children}
      </div>
    </div>
  );
}

/** A yes/no in the middle of the screen, for the things that cannot be undone. */
export function ConfirmDialog({ open, title, message, confirmLabel, cancelLabel, destructive, onConfirm, onCancel, busy }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-5">
      <button type="button" aria-label="Cancel" className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full max-w-[24rem] rounded-[18px] p-5 shadow-2xl"
        style={{ backgroundColor: Colors.background.primary }}
      >
        <p className="text-[17px] font-bold" style={{ color: Colors.text.primary }}>{title}</p>
        <p className="mt-2 whitespace-pre-line text-[13px] leading-5" style={{ color: Colors.text.secondary }}>{message}</p>
        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-[10px] border py-3 text-[13px] font-medium"
            style={{
              backgroundColor: Colors.background.secondary,
              borderColor: Colors.border.default,
              color: Colors.text.secondary,
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            data-testid="confirm-btn"
            onClick={onConfirm}
            disabled={busy}
            className="flex flex-1 items-center justify-center rounded-[10px] py-3 text-[13px] font-semibold disabled:opacity-60"
            style={{
              backgroundColor: destructive ? Colors.status.error : Colors.accent.DEFAULT,
              color: Colors.text.inverse,
            }}
          >
            {busy ? <Spinner size="sm" className="!border-white !border-b-transparent" /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The two-tab strip the board and its like are titled with. */
export function TabRow({ tabs, active, onSelect }) {
  return (
    <div className="flex gap-2">
      {tabs.map((tab) => {
        const on = active === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            data-testid={tab.testID}
            aria-pressed={on}
            onClick={() => onSelect(tab.key)}
            className="flex h-[38px] flex-1 items-center justify-center rounded-full border px-2 text-[13px]"
            style={{
              backgroundColor: on ? Colors.accent.DEFAULT : Colors.background.primary,
              borderColor: on ? Colors.accent.DEFAULT : Colors.border.default,
              color: on ? Colors.text.inverse : Colors.text.secondary,
              fontWeight: on ? 700 : 500,
            }}
          >
            <span className="truncate">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** A row of chips that scrolls sideways when there are more than fit. */
export function ChipRow({ chips, active, onSelect, testIDPrefix }) {
  return (
    <div className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {chips.map((chip) => {
        const on = active === chip.key;
        return (
          <button
            key={chip.key || 'all'}
            type="button"
            data-testid={testIDPrefix ? `${testIDPrefix}-${chip.key || 'all'}` : undefined}
            aria-pressed={on}
            onClick={() => onSelect(chip.key)}
            className="shrink-0 rounded-full border px-3 py-1.5 text-[12px] font-semibold"
            style={{
              backgroundColor: on ? Colors.accent.DEFAULT : Colors.background.primary,
              borderColor: on ? Colors.accent.DEFAULT : Colors.border.default,
              color: on ? Colors.text.inverse : Colors.text.secondary,
            }}
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}

/** The white panel a phone screen stacks its groups in. */
export function Card({ title, aside, children, className = '', testID }) {
  return (
    <section
      data-testid={testID}
      className={`rounded-[14px] border p-4 ${className}`}
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      {!!title && (
        <div className="mb-3 flex items-baseline justify-between gap-2">
          <h2 className="text-[15px] font-bold" style={{ color: Colors.text.primary }}>{title}</h2>
          {!!aside && <span className="text-[11px]" style={{ color: Colors.text.tertiary }}>{aside}</span>}
        </div>
      )}
      {children}
    </section>
  );
}

/** The one full-width action at the end of a form, the size a thumb needs. */
export function PrimaryButton({ children, onClick, disabled, busy, testID, className = '' }) {
  return (
    <button
      type="button"
      data-testid={testID}
      onClick={onClick}
      disabled={disabled || busy}
      className={`flex h-12 w-full items-center justify-center gap-2 rounded-[10px] text-[15px] font-bold disabled:opacity-60 ${className}`}
      style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
    >
      {busy ? <Spinner size="sm" className="!border-white !border-b-transparent" /> : children}
    </button>
  );
}

/**
 * A phone screen inside the desktop shell.
 *
 * An admin on a phone keeps the sidebar and its header; below them the page
 * borrows the supervisor app's ground and runs edge to edge, undoing the
 * shell's padding so the bars sit flush the way they do in the phone build.
 */
export function PhonePage({ children, testID }) {
  return (
    <div
      data-testid={testID}
      className="-m-3 flex min-h-[calc(100%+1.5rem)] flex-col md:-m-4 md:min-h-[calc(100%+2rem)]"
      style={{ backgroundColor: Colors.background.secondary }}
    >
      {children}
    </div>
  );
}

/** The title a phone list opens with, and the one thing it can create. */
export function PageHeader({ title, subtitle, action, testID }) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 pb-3 pt-5" data-testid={testID}>
      <div className="min-w-0">
        <p className="truncate text-[22px] font-bold -tracking-[0.5px]" style={{ color: Colors.text.primary }}>{title}</p>
        {!!subtitle && (
          <p className="mt-0.5 truncate text-[13px] font-medium" style={{ color: Colors.text.secondary }}>{subtitle}</p>
        )}
      </div>
      {action}
    </div>
  );
}

/** A pill that does one thing — the New button beside a list title. */
export function PillButton({ children, onClick, disabled, busy, testID, positive }) {
  return (
    <button
      type="button"
      data-testid={testID}
      onClick={onClick}
      disabled={disabled || busy}
      className="flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-[13px] font-semibold disabled:opacity-60"
      style={{ backgroundColor: positive ? Colors.semantic.positive : Colors.accent.DEFAULT, color: Colors.text.inverse }}
    >
      {busy ? <Spinner size="sm" className="!border-white !border-b-transparent" /> : children}
    </button>
  );
}

/**
 * A small bordered button on the foot of a card — edit, delete, restore. One
 * shape, so the row of actions under a stage reads the same as the row under
 * an order.
 */
export function CardAction({ icon: Icon, label, onClick, disabled, tone: name = 'neutral', testID, className = '' }) {
  const t = tone(name);
  return (
    <button
      type="button"
      data-testid={testID}
      onClick={onClick}
      disabled={disabled}
      className={`flex h-10 shrink-0 items-center gap-1 rounded-md border px-3 text-[12px] font-semibold disabled:opacity-50 ${className}`}
      style={{ backgroundColor: Colors.background.primary, borderColor: t.border, color: t.fg }}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />} {label}
    </button>
  );
}

/**
 * What a record can have done to it, as thumb-sized buttons. The row scrolls
 * sideways when there are more than fit, rather than wrapping into a wall
 * under the title.
 */
export function ActionRow({ actions, testID }) {
  return (
    <div
      data-testid={testID}
      className="-mx-4 flex gap-2 overflow-x-auto px-4 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {actions.filter(Boolean).map(({ key, label, icon: Icon, onClick, tone: name = 'neutral', primary, disabled, testID: id, badge }) => {
        const t = tone(name);
        return (
          <button
            key={key}
            type="button"
            data-testid={id}
            onClick={onClick}
            disabled={disabled}
            className="flex h-10 shrink-0 items-center gap-1.5 rounded-[10px] border px-3 text-[13px] font-semibold disabled:opacity-50"
            style={primary
              ? { backgroundColor: Colors.accent.DEFAULT, borderColor: Colors.accent.DEFAULT, color: Colors.text.inverse }
              : { backgroundColor: Colors.background.primary, borderColor: t.border, color: t.fg }}
          >
            {Icon && <Icon className="h-4 w-4 shrink-0" />}
            {label}
            {badge != null && (
              <span
                className="rounded-full px-1.5 text-[10px] tabular-nums"
                style={{ backgroundColor: Colors.semantic.critical, color: Colors.text.inverse }}
              >
                {badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Previous and next at the foot of a list that comes in pages. */
export function Pager({ page, totalPages, onPage, testID }) {
  if (!totalPages || totalPages <= 1) return null;
  const style = {
    backgroundColor: Colors.background.primary,
    borderColor: Colors.border.default,
    color: Colors.text.secondary,
  };
  return (
    <div className="mt-4 flex items-center justify-between" data-testid={testID}>
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        data-testid="prev-page-btn"
        className="flex h-10 items-center gap-1 rounded-[10px] border px-3 text-[13px] font-semibold disabled:opacity-40"
        style={style}
      >
        <ChevronLeft className="h-4 w-4" /> Prev
      </button>
      <span className="text-[12px] font-medium tabular-nums" style={{ color: Colors.text.secondary }}>
        {page} / {totalPages}
      </span>
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
        data-testid="next-page-btn"
        className="flex h-10 items-center gap-1 rounded-[10px] border px-3 text-[13px] font-semibold disabled:opacity-40"
        style={style}
      >
        Next <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
